import React from "react";
import { bool, func, string } from "prop-types";
import { Grid, TextField, Button } from "@material-ui/core";
import { parseISO, differenceInCalendarDays, format } from "date-fns";

const DateFilter = ({
  classes,
  disabledSearch = false,
  searchButtonLabel = "Search",
  fromDate,
  toDate,
  handleFromDateChange,
  handleToDateChange,
  handleDateSearch,
}) => {
  const today = format(new Date(), "yyyy-MM-dd");
  const [localFromDate, setLocalFromDate] = React.useState(fromDate || "");
  const [localToDate, setLocalToDate] = React.useState(toDate || "");
  const [dateError, setDateError] = React.useState("");
  const normalizeDate = (date) => {
    if (!date) return "";
    if (typeof date === "string") {
      return date;
    }
    if (date instanceof Date && !isNaN(date.getTime())) {
      return format(date, "yyyy-MM-dd");
    }
    return "";
  };
  const validateDateRange = (from, to) => {
    if (!from || !to) {
      setDateError("");
      return true;
    }
    const fromValue = parseISO(from);
    const toValue = parseISO(to);
    if (isNaN(fromValue.getTime()) || isNaN(toValue.getTime())) {
      setDateError("Please select valid dates.");
      return false;
    }
    if (fromValue > toValue) {
      setDateError("From date cannot be greater than To date.");
      return false;
    }
    const todayValue = parseISO(today);
    if (toValue > todayValue) {
      setDateError("To date cannot be greater than today.");
      return false;
    }
    setDateError("");
    return true;
  };
  const onFromDateChange = (e) => {
    const value = e.target.value;
    if (!value) {
      setLocalFromDate("");
      setDateError("");
      return;
    }
    let updatedToDate = localToDate;
    if (localToDate) {
      const fromValue = parseISO(value);
      const toValue = parseISO(localToDate);
      const difference = differenceInCalendarDays(toValue, fromValue);
      if (fromValue > toValue) {
        updatedToDate = value;
      }
      else if (difference > 30) {
        updatedToDate = format(
          new Date(
            fromValue.getFullYear(),
            fromValue.getMonth(),
            fromValue.getDate() + 30
          ),
          "yyyy-MM-dd"
        );
        if (parseISO(updatedToDate) > parseISO(today)) {
          updatedToDate = today;
        }
      }
    }

    setLocalFromDate(value);
    setLocalToDate(updatedToDate);
    setDateError("");
  };
  const onToDateChange = (e) => {
    const value = e.target.value;
    if (!value) {
      setLocalToDate("");
      setDateError("");
      return;
    }
    let updatedFromDate = localFromDate;
    if (localFromDate) {
      const fromValue = parseISO(localFromDate);
      const toValue = parseISO(value);
      const difference = differenceInCalendarDays(toValue, fromValue);
      if (toValue < fromValue) {
        updatedFromDate = value;
      }
      else if (difference > 30) {
        updatedFromDate = format(
          new Date(
            toValue.getFullYear(),
            toValue.getMonth(),
            toValue.getDate() - 30
          ),
          "yyyy-MM-dd"
        );
      }
    }
    setLocalFromDate(updatedFromDate);
    setLocalToDate(value);
    setDateError("");
  };
  const handleSearch = () => {
    const validFromDate = normalizeDate(localFromDate);
    const validToDate = normalizeDate(localToDate);
    if (!validFromDate || !validToDate) {
      setDateError("Please select From date and To date.");
      return;
    }
    const isValid = validateDateRange(validFromDate, validToDate);
    if (!isValid) {
      return;
    }
    handleFromDateChange(validFromDate);
    handleToDateChange(validToDate);
    if (handleDateSearch) {
      handleDateSearch({
        fromDate: validFromDate,
        toDate: validToDate,
      });
    }
  };
  return (
    <>
      <Grid
        container
        item
        xs={12}
        sm={12}
        md={12}
        className={classes.dateFilterContainer}
      >
        <TextField
          className={`${classes.field} ${classes.dateField}`}
          label="From date"
          type="date"
          value={localFromDate || ""}
          onChange={onFromDateChange}
          variant="outlined"
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            max: today,
          }}
        />
        <TextField
          className={`${classes.field} ${classes.dateField}`}
          label="To date"
          type="date"
          value={localToDate || ""}
          onChange={onToDateChange}
          variant="outlined"
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            min: localFromDate || undefined,
            max: today,
          }}
        />
        <Button
          className={classes.searchButton}
          disabled={
            disabledSearch || !localFromDate || !localToDate || !!dateError
          }
          onClick={handleSearch}
        >
          {searchButtonLabel}
        </Button>
      </Grid>
    </>
  );
};

DateFilter.propTypes = {
  classes: Object,
  disabledSearch: bool,
  searchButtonLabel: string,
  fromDate: string,
  toDate: string,
  handleFromDateChange: func.isRequired,
  handleToDateChange: func.isRequired,
  handleDateSearch: func,
};
export default DateFilter;